/**
 * MS-02 rework 2026-06-06 · `.qa-sorter` one-card-at-a-time classifier.
 *
 * Mechanic: a deck of `.sorter-item` event cards (one visible at a time) +
 * a shared row of `.sorter-btn` option buttons. User classifies the active
 * card → verdict panel reveals correct level + «почему» explanation pulled
 * from the card's own hidden `.sorter-why` → «Дальше» advances · last card
 * shows `.sorter-final` with score.
 *
 * Markup contract (per sprint-01.ts MS-02):
 *   <div class='qa-sorter' data-sorter='<id>'>
 *     <div class='sorter-head'>
 *       <span class='sorter-hint'>...</span>
 *       <span class='sorter-counter'><span class='sorter-step'>1</span> / 7</span>
 *     </div>
 *     <div class='sorter-items'>
 *       <div class='sorter-item' data-answer='qa|qc|t'>
 *         <div class='sorter-time'>09:30 · планёрка</div>
 *         <div class='sorter-text'>...event text...</div>
 *         <div class='sorter-why'>...explanation (hidden · copied into verdict)...</div>
 *       </div>
 *       ×N
 *     </div>
 *     <div class='sorter-options'>
 *       <button type='button' class='sorter-btn' data-pick='qa'>QA — правило процесса</button>
 *       <button type='button' class='sorter-btn' data-pick='qc'>QC — проверка готового</button>
 *       <button type='button' class='sorter-btn' data-pick='t'>Testing — один шаг</button>
 *     </div>
 *     <div class='sorter-verdict' aria-live='polite'></div>
 *     <div class='sorter-foot'><button type='button' class='sorter-next'>Дальше</button></div>
 *     <div class='sorter-final'><div class='sorter-score'></div><div class='sorter-moral'>...</div></div>
 *   </div>
 *
 * Progressive degradation: handler adds `.sorter-ready` on init. Without JS
 * the root keeps the no-JS layout — all cards visible stacked, options/foot
 * hidden via CSS (`.qa-sorter:not(.sorter-ready)` rules).
 *
 * Verdict labels are derived from the matching option button's text — no
 * content strings hardcoded here (content lives in the ticket body only).
 *
 * State persistence: session-only (per pedagogy · re-attempt on revisit ·
 * same as mythCards.ts).
 */
export function initSorters(root: HTMLElement): () => void {
  const sorters = Array.from(root.querySelectorAll<HTMLElement>('.qa-sorter'));
  const cleanups: Array<() => void> = [];

  for (const sorter of sorters) {
    if (sorter.dataset.sorterInit === '1') continue;
    sorter.dataset.sorterInit = '1';

    const items = Array.from(sorter.querySelectorAll<HTMLElement>('.sorter-item'));
    const buttons = Array.from(sorter.querySelectorAll<HTMLButtonElement>('.sorter-btn'));
    const verdict = sorter.querySelector<HTMLElement>('.sorter-verdict');
    const nextBtn = sorter.querySelector<HTMLButtonElement>('.sorter-next');
    const stepEl = sorter.querySelector<HTMLElement>('.sorter-step');
    const finalEl = sorter.querySelector<HTMLElement>('.sorter-final');
    const scoreEl = sorter.querySelector<HTMLElement>('.sorter-score');
    if (items.length === 0 || buttons.length === 0 || !verdict || !nextBtn) continue;

    let idx = 0;
    let score = 0;
    let answered = false;

    const labelFor = (answer: string): string => {
      const btn = buttons.find((b) => b.getAttribute('data-pick') === answer);
      return btn?.textContent?.trim() ?? answer;
    };

    const show = (i: number) => {
      answered = false;
      items.forEach((item, n) => item.classList.toggle('active', n === i));
      if (stepEl) stepEl.textContent = String(i + 1);
      verdict.innerHTML = '';
      verdict.classList.remove('visible', 'correct', 'wrong');
      nextBtn.classList.remove('visible');
      buttons.forEach((b) => {
        b.disabled = false;
        b.classList.remove('selected', 'correct', 'wrong');
      });
    };

    const onPick = (e: MouseEvent) => {
      if (answered) return;
      answered = true;
      const btn = e.currentTarget as HTMLButtonElement;
      const pick = btn.getAttribute('data-pick') ?? '';
      const item = items[idx];
      if (!item) return; // noUncheckedIndexedAccess guard · idx always in range here
      const answer = item.getAttribute('data-answer') ?? '';
      const ok = pick === answer;
      if (ok) score += 1;
      const why = item.querySelector<HTMLElement>('.sorter-why')?.innerHTML ?? '';
      verdict.innerHTML =
        (ok ? '<strong>Верно: ' : '<strong>Не совсем — это ') +
        labelFor(answer) +
        '.</strong> ' +
        why;
      verdict.classList.add('visible', ok ? 'correct' : 'wrong');
      btn.classList.add('selected', ok ? 'correct' : 'wrong');
      buttons.forEach((b) => {
        b.disabled = true;
      });
      item.classList.add('answered');
      nextBtn.textContent = idx === items.length - 1 ? 'Итог' : 'Дальше';
      nextBtn.classList.add('visible');
    };

    const onNext = () => {
      if (!answered) return;
      if (idx === items.length - 1) {
        items.forEach((item) => item.classList.remove('active'));
        sorter.classList.add('done');
        if (scoreEl) scoreEl.textContent = `Итог: ${score} из ${items.length}`;
        if (finalEl) finalEl.classList.add('visible');
        verdict.innerHTML = '';
        verdict.classList.remove('visible', 'correct', 'wrong');
        nextBtn.classList.remove('visible');
      } else {
        idx += 1;
        show(idx);
      }
    };

    buttons.forEach((btn) => btn.addEventListener('click', onPick));
    nextBtn.addEventListener('click', onNext);
    sorter.classList.add('sorter-ready');
    show(0);

    cleanups.push(() => {
      buttons.forEach((btn) => btn.removeEventListener('click', onPick));
      nextBtn.removeEventListener('click', onNext);
      delete sorter.dataset.sorterInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
